<template>
  <v-container style="padding-bottom:0px">
    <v-expansion-panels class="mt-4">
      <v-expansion-panel id="twotrees-calllogs-panel">
        <v-expansion-panel-title>2 Trees Call Records Dashboard</v-expansion-panel-title>
        <v-expansion-panel-text>
          <div class="accordion-container">
            
            <!-- Loading State -->
            <div v-if="loading" class="text-center py-8">
              <v-progress-circular indeterminate color="primary"></v-progress-circular>
              <p class="mt-2">Loading call records...</p>
            </div>

            <!-- Error State -->
            <div v-else-if="error" class="error-container">
              <v-alert type="error" class="mb-4">
                {{ error }}
              </v-alert>
              <v-btn @click="loadData" color="primary">Retry</v-btn>
            </div>

            <!-- Dashboard Content -->
            <div v-else>
              <!-- Controls and Summary -->
              <div class="summary-grid mb-6" style="display:none">
                <div class="summary-card">
                  <div class="summary-header">
                    <v-icon color="orange">mdi-alert-circle</v-icon>
                    <span class="summary-title">Alert Settings</span>
                  </div>
                  <label class="summary-label">Days without calls threshold:</label>
                  <v-select
                    v-model="alertThreshold"
                    :items="thresholdOptions"
                    item-title="text"
                    item-value="value"
                    dense
                    outlined
                    hide-details
                    class="threshold-select"
                  ></v-select>
                </div>

                <div class="summary-card">
                  <div class="summary-header">
                    <v-icon color="red">mdi-trending-down</v-icon>
                    <span class="summary-title">Numbers Needing Attention</span>
                  </div>
                  <div class="summary-number">{{ numbersNeedingAttention.length }}</div>
                  <div class="summary-subtitle">of {{ phoneNumbers.length }} total numbers</div>
                </div>

                <div class="summary-card">
                  <div class="summary-header">
                    <v-icon color="blue">mdi-calendar</v-icon>
                    <span class="summary-title">Date Range</span>
                  </div>
                  <div class="summary-subtitle">{{ dateRange[0] }} to {{ dateRange[dateRange.length - 1] }}</div>
                  <div class="summary-subtitle">Last 60 days</div>
                </div>
              </div>

              <!-- Legend -->
              <div class="legend-container mb-4">
                <h3 class="legend-title">Legend:</h3>
                <div class="legend-items">
                  <div class="legend-item">
                    <div class="legend-color no-calls"></div>
                    <span>No calls</span>
                  </div>
                  <div class="legend-item">
                    <div class="legend-color low-calls"></div>
                    <span>1-2 calls</span>
                  </div>
                  <div class="legend-item">
                    <div class="legend-color medium-calls"></div>
                    <span>3-5 calls</span>
                  </div>
                  <div class="legend-item">
                    <div class="legend-color high-calls"></div>
                    <span>6+ calls</span>
                  </div>
                </div>
              </div>

              <!-- Heat Map Table -->
              <div class="top-scroll" ref="topScroll" @scroll="syncFromTop">
                <div class="top-scroll-inner" :style="{ width: (280 + dateRange.length * 50) + 'px' }"></div>
              </div>
              <div class="heatmap-container">
                <div class="table-scroll" ref="tableScroll" @scroll="syncFromTable">
                  <table class="heatmap-table">
                    <thead>
                      <tr class="table-header">
                        <th class="phone-header">Phone Number</th>
                        <th 
                          v-for="date in dateRange" 
                          :key="date" 
                          class="date-header"
                        >
                          {{ formatDateHeader(date) }}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr 
                        v-for="phoneNumber in phoneNumbers" 
                        :key="phoneNumber.phoneNumber"
                        :class="{
                          'table-row': true,
                          'needs-attention': getConsecutiveNoCalls(phoneNumber.phoneNumber) >= alertThreshold
                        }"
                      >
                        <td class="phone-cell">
                          <div class="phone-info">
                            <div class="phone-number-line">
                              <v-icon small class="mr-2">mdi-phone</v-icon>
                              <span class="phone-number">{{ phoneNumber.phoneNumber }}</span>
                            </div>
                            <div class="friendly-name">{{ phoneNumber.friendlyName }}</div>
                          </div>
                        </td>
                        <td 
                          v-for="date in dateRange" 
                          :key="date"
                          class="date-cell"
                        >
                          <div
                            :class="getCellClass(phoneNumber.phoneNumber, date)"
                            @click="handleCellClick(phoneNumber.phoneNumber, date)"
                            :title="`${getCallCount(phoneNumber.phoneNumber, date)} calls on ${date}`"
                          >
                            {{ getCallCount(phoneNumber.phoneNumber, date) || '' }}
                          </div>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </v-expansion-panel-text>
      </v-expansion-panel>
    </v-expansion-panels>

    <!-- Call Details Dialog -->
    <v-dialog v-model="showDetails" max-width="500px">
      <v-card v-if="selectedCell">
        <v-card-title class="headline">
          <span class="call-details-title">Call Details</span>
          <v-spacer></v-spacer>
          <v-btn icon @click="showDetails = false" class="call-details-close-btn">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        
        <v-card-text>
          <div class="mb-4">
            <p class="font-weight-bold">{{ selectedCell.phoneNumber }}</p>
            <p class="text--secondary font-italic">{{ selectedCell.friendlyName }}</p>
            <p class="text--secondary">{{ selectedCell.date }}</p>
            <p class="text-h6 primary--text">{{ selectedCell.callCount }} total calls</p>
          </div>

          <div v-if="selectedCell.callCount > 0" class="call-details">
            <h4 class="mb-2">Individual Calls:</h4>
            <div class="call-list">
              <div 
                v-for="(call, idx) in sortedCalls" 
                :key="idx"
                class="call-item"
              >
                <div class="call-info">
                  <span class="call-from">{{ call.From }}</span>
                  <span class="call-time">{{ formatTime(call['Date Created']) }}</span>
                </div>
                <div class="call-duration">Duration: {{ call.Duration }}s</div>
              </div>
            </div>
          </div>
          
          <div v-else class="no-calls">
            No calls received on this date
          </div>
        </v-card-text>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import moment from 'moment-timezone';

const TZ = 'America/Los_Angeles';

export default {
  name: 'CompanyAccountTwoTreesCallLogs',

  data() {
    return {
      loading: false,
      error: null,
      phoneNumbers: [],
      callRecords: [],
      callDataByNumber: {},
      alertThreshold: 3,
      showDetails: false,
      selectedCell: null,
      thresholdOptions: [
        { text: '1 day', value: 1 },
        { text: '3 days', value: 3 },
        { text: '7 days', value: 7 },
        { text: '14 days', value: 14 }
      ]
    };
  },

  computed: {
    dateRange() {
      const dates = [];
      const today = moment().tz(TZ);
      for (let i = 60; i >= 0; i--) {  // Includes today
        dates.push(today.clone().subtract(i, 'days').format('YYYY-MM-DD'));
      }
      return dates;
    },

    numbersNeedingAttention() {
      return this.phoneNumbers.filter(number => 
        this.getConsecutiveNoCalls(number.phoneNumber) >= this.alertThreshold
      );
    },

    sortedCalls() {
      if (!this.selectedCell || !this.selectedCell.calls) {
        return [];
      }
      
      return [...this.selectedCell.calls].sort((a, b) => {
        const dateA = new Date(a['Date Created']);
        const dateB = new Date(b['Date Created']);
        return dateA - dateB; // Ascending order (earliest first)
      });
    }
  },

  async mounted() {
    await this.loadData();
  },

  methods: {
    async loadData() {
      this.loading = true;
      this.error = null;

      try {
        // Load phone numbers and call records in parallel
        const [phoneResponse, callResponse] = await Promise.all([
          this.fetchPhoneNumbers(),
          this.fetchCallRecords()
        ]);

        this.phoneNumbers = phoneResponse.data;
        this.callRecords = callResponse.data;
        
        // Process call data by phone number and date
        this.processCallData();

      } catch (error) {
        console.error('Error loading call data:', error);
        this.error = 'Failed to load call records. Please try again.';
      } finally {
        this.loading = false;
      }
    },

    async fetchPhoneNumbers() {
      const response = await fetch('/portaldb/db/customDb/phoneNumbers');
      const data = await response.json();
      
      if (!response.ok || !data.success) {
        throw new Error(data.message || 'Failed to fetch phone numbers');
      }
      return data;
    },

    async fetchCallRecords() {
      const startDate = this.dateRange[0];
      
      // Request one extra UTC day so records like 4/7 00:30 UTC (= 4/6 PT) aren't excluded
      const endDate = moment().tz(TZ).add(1, 'day').format('YYYY-MM-DD');
      
      const url = new URL('/portaldb/db/customDb/callLogs', window.location.origin);
      url.searchParams.append('startDate', startDate);
      url.searchParams.append('endDate', endDate);
      
      const response = await fetch(url);
      const data = await response.json();
      
      if (!response.ok || !data.success) {
        throw new Error(data.message || 'Failed to fetch call records');
      }
      return data;
    },

    processCallData() {
      // Initialize data structure
      this.callDataByNumber = {};
      
      this.phoneNumbers.forEach(number => {
        this.callDataByNumber[number.phoneNumber] = {};
        this.dateRange.forEach(date => {
          this.callDataByNumber[number.phoneNumber][date] = [];
        });
      });

      // Group call records by phone number and date
      this.callRecords.forEach(call => {
        const dateCreated = moment(call['Date Created']).tz(TZ).format('YYYY-MM-DD');
        const toNumber = call.To || call['Called Via'];
        
        if (this.callDataByNumber[toNumber] && this.callDataByNumber[toNumber][dateCreated]) {
          this.callDataByNumber[toNumber][dateCreated].push(call);
        }
      });
    },

    getCallCount(phoneNumber, date) {
      return this.callDataByNumber[phoneNumber]?.[date]?.length || 0;
    },

    getCellClass(phoneNumber, date) {
      const callCount = this.getCallCount(phoneNumber, date);
      let colorClass = 'no-calls';
      
      if (callCount === 0) colorClass = 'no-calls';
      else if (callCount <= 2) colorClass = 'low-calls';
      else if (callCount <= 5) colorClass = 'medium-calls';
      else colorClass = 'high-calls';
      
      return `call-cell ${colorClass}`;
    },

    getConsecutiveNoCalls(phoneNumber) {
      let consecutiveDays = 0;
      for (let i = this.dateRange.length - 1; i >= 0; i--) {
        const date = this.dateRange[i];
        if (this.getCallCount(phoneNumber, date) === 0) {
          consecutiveDays++;
        } else {
          break;
        }
      }
      return consecutiveDays;
    },

    handleCellClick(phoneNumber, date) {
      const callCount = this.getCallCount(phoneNumber, date);
      const calls = this.callDataByNumber[phoneNumber]?.[date] || [];
      
      // Find the phone number object to get the friendly name
      const phoneNumberObj = this.phoneNumbers.find(p => p.phoneNumber === phoneNumber);
      
      this.selectedCell = {
        phoneNumber,
        friendlyName: phoneNumberObj?.friendlyName || '',
        date,
        callCount,
        calls
      };
      this.showDetails = true;
    },

    syncFromTop() {
      const top = this.$refs.topScroll;
      const table = this.$refs.tableScroll;
      if (table) table.scrollLeft = top.scrollLeft;
    },

    syncFromTable() {
      const top = this.$refs.topScroll;
      const table = this.$refs.tableScroll;
      if (top) top.scrollLeft = table.scrollLeft;
    },

    formatTime(dateString) {
      return moment(dateString).format('h:mm A');
    },

    formatDateHeader(date) {
      // Split the date string directly instead of using Date constructor to avoid timezone issues
      const [, month, day] = date.split('-');
      return `${parseInt(month)}/${parseInt(day)}`;
    }

  }
};
</script>

<style scoped>
.accordion-container {
  background-color: #f5f5dc;
  padding: 20px;
  border-radius: 8px;
}

.summary-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 16px;
}

.summary-card {
  background-color: white;
  padding: 16px;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

.summary-header {
  display: flex;
  align-items: center;
  margin-bottom: 8px;
}

.summary-title {
  font-weight: 600;
  margin-left: 8px;
}

.summary-label {
  font-size: 14px;
  color: #666;
  margin-bottom: 4px;
  display: block;
}

.summary-number {
  font-size: 32px;
  font-weight: bold;
  color: #d32f2f;
}

.summary-subtitle {
  font-size: 14px;
  color: #666;
}

.threshold-select {
  max-width: 150px;
}

.legend-container {
  background-color: white;
  padding: 16px;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

.legend-title {
  font-weight: 600;
  margin-bottom: 8px;
}

.legend-items {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
}

.legend-item {
  display: flex;
  align-items: center;
  font-size: 14px;
}

.legend-color {
  width: 16px;
  height: 16px;
  border-radius: 4px;
  border: 1px solid #ddd;
  margin-right: 8px;
}

.legend-color.no-calls {
  border-color: #ddd;
}

.legend-color.low-calls {
  background-color: #fff3cd;
  border-color: #ffeaa7;
}

.legend-color.medium-calls {
  background-color: #c8e6c9;
  border-color: #81c784;
}

.legend-color.high-calls {
  background-color: #a5d6a7;
  border-color: #66bb6a;
}

.heatmap-container {
  background-color: white;
  border-radius: 0 0 8px 8px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  overflow: hidden;
}

.top-scroll {
  overflow-x: auto;
  overflow-y: hidden;
  height: 17px;
  background-color: white;
  border-radius: 8px 8px 0 0;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

.top-scroll-inner {
  height: 1px;
}

.top-scroll-inner {
  height: 1px;
}

.table-scroll {
  overflow-x: auto;
}

.heatmap-table {
  width: 100%;
  border-collapse: collapse;
}

.table-header {
  background-color: #f5f5f5;
}

.phone-header {
  position: sticky;
  left: 0;
  background-color: #f5f5f5;
  padding: 12px 16px;
  text-align: left;
  font-weight: 600;
  border-right: 1px solid #ddd;
  min-width: 280px;
}

.date-header {
  padding: 12px 8px;
  text-align: center;
  font-size: 12px;
  font-weight: 600;
  min-width: 50px;
}

.alert-header {
  padding: 12px 16px;
  text-align: center;
  font-weight: 600;
}

.table-row {
  border-bottom: 1px solid #eee;
}

.table-row:hover {
  background-color: #f9f9f9;
}

.table-row.needs-attention {
}

.phone-cell {
  position: sticky;
  left: 0;
  background-color: white;
  padding: 8px 16px;
  border-right: 1px solid #ddd;
  font-size: 14px;
  vertical-align: top;
}

.table-row.needs-attention .phone-cell {
}

.phone-info {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.phone-number-line {
  display: flex;
  align-items: center;
}

.phone-number {
  font-family: monospace;
  font-weight: 600;
  font-size: 14px;
}

.friendly-name {
  font-size: 12px;
  color: #666;
  font-style: italic;
  line-height: 1.2;
  margin-left: 24px; /* Align with phone number (icon width + margin) */
}

.date-cell {
  padding: 4px;
  text-align: center;
  width: 50px;
  min-width: 50px;
  max-width: 50px;
}

.call-cell {
  width: 32px;
  height: 32px;
  min-width: 32px;
  min-height: 32px;
  max-width: 32px;
  max-height: 32px;
  border-radius: 4px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 600;
  margin: 0 auto;
  transition: transform 0.2s ease;
  box-sizing: border-box;
  flex-shrink: 0;
  flex-grow: 0;
  border: 1px solid #ddd;
}

.call-cell:hover {
  transform: scale(1.1);
}

.call-cell.no-calls {
  border: 1px solid #ddd;
  width: 32px;
  height: 32px;
}

.call-cell.low-calls {
  background-color: #fff3cd;
  border: 1px solid #ffeaa7;
  width: 32px;
  height: 32px;
}

.call-cell.medium-calls {
  background-color: #c8e6c9;
  border: 1px solid #81c784;
  width: 32px;
  height: 32px;
}

.call-cell.high-calls {
  background-color: #a5d6a7;
  border: 1px solid #66bb6a;
  width: 32px;
  height: 32px;
}

.alert-cell {
  padding: 12px 16px;
  text-align: center;
}

.alert-indicator {
  display: flex;
  align-items: center;
  justify-content: center;
}

.alert-days {
  margin-left: 4px;
  font-size: 12px;
  color: #d32f2f;
}

.error-container {
  text-align: center;
  padding: 20px;
}

.call-details {
  max-height: 300px;
  overflow-y: auto;
}

.call-list {
  space-y: 8px;
}

.call-item {
  background-color: #f5f5f5;
  padding: 12px;
  border-radius: 4px;
  margin-bottom: 8px;
}

.call-info {
  display: flex;
  justify-content: space-between;
  font-size: 14px;
  margin-bottom: 4px;
}

.call-from {
  font-family: monospace;
  font-weight: 600;
}

.call-time {
  color: #666;
}

.call-duration {
  font-size: 12px;
  color: #666;
}

.no-calls {
  text-align: center;
  color: #666;
  padding: 0px;
  font-style: italic;
}

:deep(.v-expansion-panels > .v-expansion-panel > .v-expansion-panel-title) {
  font-size: 1.25rem !important;
  font-weight: 600 !important;
  color: #ffffff !important;
  background-color: #d2b48c !important;
  border: 1px solid #d2b48c !important;
}

:deep(.v-expansion-panels > .v-expansion-panel > .v-expansion-panel-title .v-expansion-panel-title__icon .v-icon) {
  color: #ffffff !important;
}

:deep(.v-expansion-panel-text) {
  background-color: #f5f5dc !important;
  border: 1px solid #d2b48c !important;
  border-top: none !important;
}

#twotrees-calllogs-panel {
  margin-bottom: 8px;
}

.call-details-close-btn {
  position: absolute;
  right: 6px;
  top: 6px;  
  width: 32px;
  height: 32px;
}

@media (max-width: 768px) {
  .summary-grid {
    grid-template-columns: 1fr;
  }
  
  .legend-items {
    flex-direction: column;
    gap: 8px;
  }
  
  .phone-header {
    min-width: 200px;
  }
  
  .phone-cell {
    font-size: 12px;
  }
  
  .phone-number {
    font-size: 12px;
  }
  
  .friendly-name {
    font-size: 10px;
    margin-left: 20px;
  }
}
</style>
